import { useCallback } from 'react';
import { ClipObject } from '../../../../context/clips';
import {
  ClipDeletionFragment,
  ClipDeletionFragmentDoc,
} from '../../operation.generated';
import useOptimisticDeleteClip from '../useOptimisticDeleteClip';
import useRestoreClip from '../useRestoreClip';
import { useStore } from './common';
import { toast } from '../../../Toaster';
import { getApolloMutationErrorMessage } from '../../../../util/apolloError';

const useDeleteClip = () => {
  const [optimisticDeleteMutation] = useOptimisticDeleteClip();
  const [restoreMutation] = useRestoreClip();
  const selectedClipIds = useStore(state => state.selectedClipIds);
  const setSelectedClipIds = useStore(state => state.setSelectedClipIds);
  const deleteClip = useCallback(
    async (clip: ClipObject) => {
      if (clip.streamRequest) {
        clip.streamRequest.abort();
      }

      const deletionPromise = new Promise<void>((resolve, reject) => {
        let updateCount = 0;
        optimisticDeleteMutation({
          variables: { inputs: { clipId: clip.id } },
          // for optimistic responses `update` is called twice, for the optimistic response
          // and when the server actually responds
          // https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
          update: (proxy, { data }) => {
            updateCount += 1;
            const mutationCompleted = updateCount === 2;
            const isDeleted = data?.deleteClip.success || false;
            // Update Apollo cache state (if entity exists)
            const cachedClip = proxy.readFragment<ClipDeletionFragment>({
              id: proxy.identify({
                id: clip.id,
                __typename: 'Clip',
              }),
              fragment: ClipDeletionFragmentDoc,
            });
            if (cachedClip) {
              proxy.writeFragment<ClipDeletionFragment>({
                id: proxy.identify({
                  id: cachedClip?.id,
                  __typename: 'Clip',
                }),
                fragment: ClipDeletionFragmentDoc,
                data: {
                  ...cachedClip,
                  isDeleted,
                },
              });
            }
            // Handle promise
            if (data?.deleteClip.success === false) {
              toast({
                message: data.deleteClip.message,
                type: 'error',
              });
              reject(data.deleteClip.message);
            } else if (mutationCompleted) {
              const id = data?.deleteClip?.clip?.id;
              if (id) {
                setSelectedClipIds(selectedClipIds.remove(id));
              }
              resolve();
            }
          },
        }).catch(error => {
          const message = getApolloMutationErrorMessage(
            error,
            'Failed to delete clip'
          );
          toast({
            message,
            type: 'error',
          });
          reject(message);
        });
      });

      toast({
        message: clip.name ? `Deleted clip ${clip.name}.` : 'Deleted clip.',
        cta: {
          label: 'undo',
          onClick: () => {
            deletionPromise
              .then(() => {
                restoreMutation({
                  variables: { inputs: { clipId: clip.id } },
                  update: (proxy, { data }) => {
                    if (!data?.restoreClip.success) {
                      toast({
                        message: 'Error restoring clip',
                        type: 'error',
                      });
                    }
                  },
                });
              })
              .catch(() => {
                // handled inside of optimistic delete
              });
          },
        },
      });
    },
    [
      optimisticDeleteMutation,
      restoreMutation,
      selectedClipIds,
      setSelectedClipIds,
    ]
  );
  return deleteClip;
};

export default useDeleteClip;
